import { EventEmitter } from "events";
import isBoolean from "lodash/isBoolean";
import isPlainObject from "lodash/isPlainObject";

import SelectionManagerEventType from "./SelectionManagerEventType";

type WeakMapEntry<T extends object> = [T, boolean];

function isWeakMapEntry<T extends object>(
  value: any
): value is WeakMapEntry<T> {
  const weakMapEntry = value as WeakMapEntry<T>;

  return (
    Array.isArray(weakMapEntry) &&
    isPlainObject(weakMapEntry[0]) &&
    isBoolean(weakMapEntry[1])
  );
}

function isWeakMapEntries<T extends object>(
  value: any
): value is WeakMapEntry<T>[] {
  const weakMapEntries = value as WeakMapEntry<T>[];

  if (!Array.isArray(weakMapEntries)) return false;

  for (let i = 0; i < weakMapEntries.length; i++) {
    if (!isWeakMapEntry(weakMapEntries[i])) return false;
  }

  return true;
}

class SelectionManager<T extends object> extends EventEmitter {
  private readonly _store: WeakMap<T, boolean>;

  constructor(items?: T[] | WeakMapEntry<T>[]) {
    super();

    if (items === undefined) {
      this._store = new WeakMap();
      return;
    }

    if (!Array.isArray(items)) {
      this._store = new WeakMap();
      return;
    }

    if (isWeakMapEntries(items)) {
      this._store = new WeakMap(items);
      return;
    }

    this._store = new WeakMap();
    items.forEach((item) => this._store.set(item, false));
  }

  private delete(item: T) {
    return this._store.delete(item);
  }

  private get(item: T) {
    return this._store.get(item);
  }

  private has(item: T) {
    return this._store.has(item);
  }

  private set(item: T, selected: boolean = false) {
    return this._store.set(item, selected);
  }

  public add(item: T) {
    this.set(item);

    this.emit(SelectionManagerEventType.ADD, item);

    return this;
  }

  public remove(item: T) {
    const removed = this.delete(item);

    if (removed) this.emit(SelectionManagerEventType.REMOVE, item);

    return removed;
  }

  public isSelected(item: T): boolean | undefined {
    return this.get(item);
  }

  public select(item: T) {
    const has = this.has(item);

    if (has === false) return;

    this.set(item, true);

    this.emit(SelectionManagerEventType.CHANGE, item);
  }

  public deselect(item: T) {
    const has = this.has(item);

    if (has === false) return;

    this.set(item, false);

    this.emit(SelectionManagerEventType.CHANGE, item);
  }

  public toggle(item: T) {
    const selected = this.isSelected(item);

    if (selected === undefined) return;

    this.set(item, !selected);

    this.emit(SelectionManagerEventType.CHANGE, item);
  }
}

export default SelectionManager;
